import type { FC } from "react"

import type {
  ConsumptionInsightsReason,
  ConsumptionInsightsUnavailableEvidenceDto,
  SolarFitAnnualTotalsViewDto,
  SolarFitViewDto,
} from "~/api/dto/proposal-consumption.dto"

import { formatKwh } from "./chart-theme"
import { ProductionOverlayChart } from "./ProductionOverlayChart"
import { insufficientEvidenceCopy } from "./TypicalDayTab"

export const SOLAR_FIT_NO_INTERVAL_COPY = `Solar fit compares real hourly usage with the proposed system. Request Green Button data to unlock it.`

export const SOLAR_FIT_NO_DESIGN_COPY = `Solar fit needs the proposal's modeled production. Finish system design to unlock this view.`

export const SOLAR_FIT_GRID_IMPORT_COPY = `This profile reflects grid import rather than native household load, so Solar fit is not available. Other insights may still be available when their evidence qualifies.`

export const SOLAR_FIT_INSUFFICIENT_DAYS_COPY = `Green Button data is connected, but fewer than seven complete local days align with the proposed production. Solar fit needs more observed days before it can be shown.`

export const SOLAR_FIT_UNALIGNED_PRODUCTION_COPY = `Green Button data is connected, but the observed usage days do not align with the proposal's modeled production calendar. Solar fit cannot build an overlay from this evidence.`

export const SOLAR_FIT_INCOMPLETE_ANNUAL_TOTALS_COPY = `Annual self-consumption, export, and grid totals need a complete eligible year of usage aligned with modeled production. The daily overlay below still uses the observed days that do align.`

const formatCoverageDate = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(`en-US`, {
    month: `short`,
    day: `numeric`,
    year: `numeric`,
    timeZone: `UTC`,
  }).format(date)
}

export const formatSolarFitBasisCopy = (viewModel: SolarFitViewDto): string => {
  const channelLabel =
    viewModel.channelBasis === `netKwh` ? `net usage` : `import consumption`
  const statusLabel =
    viewModel.evidenceStatus === `complete` ? `Complete` : `Partial`
  return `${statusLabel} ${channelLabel} · ${viewModel.observedDayCount} aligned days · ${viewModel.coveragePercent}% of a year · ${formatCoverageDate(viewModel.coverageStart)} – ${formatCoverageDate(viewModel.coverageEnd)}`
}

type InsightTileProps = {
  hint: string
  label: string
  value: string
}

const InsightTile: FC<InsightTileProps> = ({ hint, label, value }) => (
  <div className="rounded-xl border border-border bg-background px-5 py-4">
    <p className="font-display text-xs font-semibold tracking-wide text-muted uppercase">
      {label}
    </p>
    <p className="mt-2 font-display text-3xl font-bold tabular-nums text-foreground">
      {value}
    </p>
    <p className="mt-1 text-sm text-muted">{hint}</p>
  </div>
)

const SOLAR_FIT_EMPTY_COPY_BY_REASON: Partial<
  Record<ConsumptionInsightsReason, string>
> = {
  no_design_series: SOLAR_FIT_NO_DESIGN_COPY,
  grid_import_basis: SOLAR_FIT_GRID_IMPORT_COPY,
  insufficient_observed_days: SOLAR_FIT_INSUFFICIENT_DAYS_COPY,
  unaligned_production: SOLAR_FIT_UNALIGNED_PRODUCTION_COPY,
}

export const SolarFitEmptyState: FC<{
  reason?: ConsumptionInsightsReason
  evidence?: ConsumptionInsightsUnavailableEvidenceDto
}> = ({ evidence, reason }) => {
  const copy =
    reason === `insufficient_observed_days`
      ? insufficientEvidenceCopy(
          `Solar fit`,
          evidence,
          SOLAR_FIT_INSUFFICIENT_DAYS_COPY,
        )
      : ((reason ? SOLAR_FIT_EMPTY_COPY_BY_REASON[reason] : undefined) ??
        SOLAR_FIT_NO_INTERVAL_COPY)

  return (
    <div className="rounded-xl border border-dashed border-border bg-surface px-6 py-10 text-center">
      <p className="mx-auto max-w-lg font-display text-base font-semibold text-foreground">
        {copy}
      </p>
    </div>
  )
}

const SolarFitAnnualTotals: FC<{
  totals: SolarFitAnnualTotalsViewDto
  channelBasis: SolarFitViewDto[`channelBasis`]
}> = ({ totals, channelBasis }) => {
  const totalProduced = totals.selfConsumedKwh + totals.exportedKwh
  const totalConsumed = totals.selfConsumedKwh + totals.gridKwh
  const selfConsumedShare =
    totalConsumed === 0
      ? 0
      : Math.round((totals.selfConsumedKwh / totalConsumed) * 100)
  const exportedShare =
    totalProduced === 0
      ? 0
      : Math.round((totals.exportedKwh / totalProduced) * 100)
  const gridShare =
    totalConsumed === 0 ? 0 : Math.round((totals.gridKwh / totalConsumed) * 100)
  const usageNoun = channelBasis === `netKwh` ? `net usage` : `home usage`

  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <InsightTile
        label="Self-consumed"
        value={formatKwh(totals.selfConsumedKwh)}
        hint={`${selfConsumedShare}% of ${usageNoun} covered by solar`}
      />
      <InsightTile
        label="Exported"
        value={formatKwh(totals.exportedKwh)}
        hint={`${exportedShare}% of solar output sent to the grid`}
      />
      <InsightTile
        label="Grid import"
        value={formatKwh(totals.gridKwh)}
        hint={`${gridShare}% of ${usageNoun} still from the grid`}
      />
    </div>
  )
}

export const SolarFitTab: FC<{ viewModel: SolarFitViewDto }> = ({
  viewModel,
}) => {
  const overlayCopy =
    viewModel.evidenceStatus === `partial`
      ? `Average day overlay of the proposed system against observed hourly usage for the aligned days below. Solar-only — battery shifting is not included.`
      : `Average day overlay of the proposed system against measured hourly usage. Solar-only — battery shifting is not included.`

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted">{formatSolarFitBasisCopy(viewModel)}</p>
      {viewModel.annualTotals.available ? (
        <SolarFitAnnualTotals
          totals={viewModel.annualTotals.data}
          channelBasis={viewModel.channelBasis}
        />
      ) : (
        <div className="rounded-xl border border-dashed border-border bg-surface px-5 py-4">
          <p className="font-display text-sm font-semibold text-foreground">
            Annual totals unavailable
          </p>
          <p className="mt-1 text-sm text-muted">
            {SOLAR_FIT_INCOMPLETE_ANNUAL_TOTALS_COPY}
          </p>
        </div>
      )}
      <div>
        <h3 className="font-display text-sm font-semibold text-foreground">
          Production vs consumption
        </h3>
        <p className="mt-1 text-sm text-muted">{overlayCopy}</p>
        <div className="mt-4">
          <ProductionOverlayChart
            consumptionKw={viewModel.consumptionKw}
            productionKw={viewModel.productionKw}
          />
        </div>
      </div>
    </div>
  )
}
